'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ModalProps } from '@/components/modal/types';
import { ModalProvider, useModal } from '@/components/modal/ModalContext';
import { Button } from '@/components/button/Button';
import { renameDomProps } from '@/utils/sanitizeDomProps';
import { useTranslations } from '@/lib/locale-context';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scroll-lock';
import './style.css';

// Modal size class mappings
export const modalSizeClasses: Record<'small' | 'medium' | 'large', string> = {
  small: 'sm:w-full md:w-full lg:w-1/3',
  medium: 'sm:w-full md:w-full lg:w-1/2',
  large: 'sm:w-full md:w-full lg:w-3/4',
};

const Modal = ({
  isOpen,
  onClose,
  size = 'medium',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  ariaLabel,
  children,
  className,
  classNameClose,
  ...rest
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedElement = useRef<Element | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const renamedProps = renameDomProps(rest);

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
      lastFocusedElement.current = document.activeElement;
      const timeout = setTimeout(() => setIsVisible(true), 50);
      modalRef.current?.focus();
      return () => clearTimeout(timeout);
    } else {
      setIsVisible(false);
      unlockBodyScroll();
      const timeout = setTimeout(() => {
        if (lastFocusedElement.current) {
          (lastFocusedElement.current as HTMLElement).focus();
        }
        setIsClosing(false);
      }, 300);
      return () => {
        clearTimeout(timeout);
        unlockBodyScroll();
      };
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose, setIsClosing, setIsVisible]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEsc) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeOnEsc, handleClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!modalRef.current) return;
    if (
      !modalRef.current.contains(event.target as Node) &&
      closeOnOverlayClick
    ) {
      handleClose();
    }
  };

  if (!isOpen && !isClosing) return null;

  const sizeClass = modalSizeClasses[size] || modalSizeClasses.medium;

  return (
    <ModalProvider onClose={handleClose}>
      <div
        className={`smzh-modal modal-overlay fixed inset-0 z-50 flex items-center bg-brand-900 bg-opacity-80 transition-opacity duration-300 ease-in-out ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleOverlayClick}
        {...renamedProps}
      >
        <div
          className={`${className} modal-content relative mx-6 max-h-[90vh] w-full max-w-[900px] overflow-y-auto rounded-2xl bg-transparent py-4 shadow-lg transition-all duration-300 ease-in-out sm:h-auto md:mx-auto ${sizeClass} ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
          id={ariaLabel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabel}
          aria-label={ariaLabel}
          aria-describedby="modal-body"
          ref={modalRef}
          tabIndex={-1}
        >
          <div className="modal-inner relative max-h-[85vh] w-full">
            {children}
          </div>
        </div>
      </div>
    </ModalProvider>
  );
};

const ModalHeader = ({
  children,
  classNameClose,
}: {
  children: React.ReactNode;
  classNameClose?: string;
}) => {
  const { onClose } = useModal();
  const { t } = useTranslations();
  return (
    <div
      id="modal-header"
      className="modal-header flex items-center justify-between"
    >
      {children}
      <Button
        variant={'tertiary'}
        onClick={onClose}
        aria-label={t('ui.closeModal')}
        leftIcon={{ name: 'x', color: 'currentColor' }}
        className={`z-100 absolute right-0 top-0 rounded-full p-1 text-white ${classNameClose}`}
      />
    </div>
  );
};

const ModalBody = ({ children }: { children: React.ReactNode }) => (
  <div id="modal-body" aria-live="polite">
    {children}
  </div>
);

const ModalFooter = ({ children }: { children: React.ReactNode }) => (
  <div id="modal-footer" className="modal-footer border-t pt-2">
    {children}
  </div>
);

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
